import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import CheckIcon from 'components/material-symbol/icons/Check'
import ConfirmationNumberIcon from 'components/material-symbol/icons/ConfirmationNumber'
import { DownloadMyPGAsection } from 'components/download-mypga-section'
import { getInitials } from 'lib/text-display'
import CancellationCallout from 'pages/schedule/components/cancellation-callout'
import ReviewSummaryCard from 'pages/schedule/ReviewBooking/redesign/review-summary-card'
import { formatSlotRange } from 'pages/schedule/ReviewBooking/redesign/utils'
import { formatCurrency } from 'pages/schedule/utils'
import useStyles from './styles'
import { getCoachFirstName, getPaidNote } from './utils'

const NextSteps = ({ steps }) => {
  const classes = useStyles()

  return (
    <ol className={classes.steps} aria-label="What happens next">
      {steps.map(({ title, caption }, index) => (
        <li key={title} className={classes.step}>
          <span
            className={clsx(
              classes.stepNumber,
              index === 0 && classes.stepNumberActive,
            )}
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <div>
            <p className={classes.stepTitle}>{title}</p>
            {caption && <p className={classes.stepCaption}>{caption}</p>}
          </div>
        </li>
      ))}
    </ol>
  )
}

NextSteps.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({ title: PropTypes.string, caption: PropTypes.string }),
  ).isRequired,
}

export const getNextSteps = ({ coach, facility, email }) => [
  {
    title: 'Check your email',
    caption: email
      ? `We sent your confirmation to ${email}.`
      : 'You will receive a confirmation email shortly.',
  },
  {
    title: 'Get ready for your lesson',
    caption: facility?.name
      ? `Meet ${getCoachFirstName(coach)} at ${facility.name}.`
      : `${getCoachFirstName(coach)} is looking forward to meeting you.`,
  },
]

// Confirmation for a booked private lesson, shown with
// FLAG_FEAT_PRIVATE_BOOKING_REDESIGN on. It is presentational, so the paid
// return page, the free / pay-at-location summary and the lesson credit summary
// all feed it the same props. Like the group event and Virtual Coaching
// confirmations it is a navy tint check, a serif navy title, a summary card, a
// numbered "what happens next" list and an outlined + contained button pair.
const PrivateLessonConfirmation = ({
  coach,
  lessonTitle,
  durationInMinutes,
  facility,
  startDateTime,
  studentName,
  email,
  payment,
  credit,
  coachIsViewingOwnSchedule,
  onViewOfferings,
  onBookAnotherSession,
}) => {
  const classes = useStyles()
  const slotLabel = formatSlotRange(
    startDateTime,
    durationInMinutes,
    facility?.timezone,
  )
  const hasTotal = payment?.amountInCents > 0
  const lessonType = {
    title: lessonTitle,
    durationInMinutes,
    facilities: facility ? [facility] : [],
  }

  return (
    <div className={classes.page} data-testid="private-lesson-confirmation">
      <div className={classes.column}>
        <div className={classes.hero}>
          <div className={classes.iconCircle} aria-hidden="true">
            <CheckIcon />
          </div>
          <Typography variant="h4" component="h1" className={classes.title}>
            You&apos;re booked!
          </Typography>
          <p className={classes.subtitle}>
            {coach?.name
              ? `Your lesson with ${coach.name} is confirmed.`
              : 'Your lesson is confirmed.'}
          </p>
        </div>

        <div className={classes.stack}>
          <div>
            <ReviewSummaryCard
              lessonType={lessonType}
              coach={coach || {}}
              slotLabel={slotLabel}
              totalLabel={
                hasTotal ? formatCurrency(payment.amountInCents / 100) : null
              }
              totalNote={hasTotal ? getPaidNote(payment) : null}
              showCallout={false}
            />
            {studentName && (
              <div className={classes.whoRow}>
                <Avatar className={classes.whoAvatar} aria-hidden="true">
                  {getInitials(studentName)}
                </Avatar>
                <div className={classes.whoText}>
                  <p className={classes.whoName} data-testid="student-name">
                    {studentName}
                  </p>
                  <p className={classes.whoSub}>Student</p>
                </div>
              </div>
            )}
            {credit && (
              <div className={classes.creditNote} data-testid="credit-note">
                <span className={classes.creditIcon} aria-hidden="true">
                  <ConfirmationNumberIcon />
                </span>
                <p>
                  <strong>
                    We applied one of the lessons from your pack.{' '}
                  </strong>
                  <span className={classes.creditCount}>
                    {credit.remaining === 1
                      ? 'You have 1 lesson left.'
                      : `You have ${credit.remaining} lessons left.`}
                  </span>
                </p>
              </div>
            )}
          </div>

          <section className={classes.card} aria-labelledby="what-next-title">
            <Typography
              id="what-next-title"
              variant="h5"
              component="h2"
              className={classes.cardTitle}
            >
              What Happens Next
            </Typography>
            <NextSteps steps={getNextSteps({ coach, facility, email })} />
            <CancellationCallout
              canStudentCancel={coach?.canStudentCancel}
              cancellationPolicy={coach?.cancellationPolicy}
              className={classes.callout}
            />
          </section>

          <div className={classes.actions}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
              onClick={onViewOfferings}
            >
              {coachIsViewingOwnSchedule
                ? 'View My Schedule'
                : 'View Coach Offerings'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={onBookAnotherSession}
            >
              Book Another Session
            </Button>
          </div>
        </div>

        <div className={classes.download}>
          <DownloadMyPGAsection />
        </div>
      </div>
    </div>
  )
}

PrivateLessonConfirmation.propTypes = {
  coach: PropTypes.shape({
    name: PropTypes.string,
    canStudentCancel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    cancellationPolicy: PropTypes.string,
    coachProfile: PropTypes.object,
  }),
  lessonTitle: PropTypes.string,
  durationInMinutes: PropTypes.number,
  facility: PropTypes.shape({
    name: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    timezone: PropTypes.string,
  }),
  startDateTime: PropTypes.string,
  studentName: PropTypes.string,
  email: PropTypes.string,
  payment: PropTypes.shape({
    amountInCents: PropTypes.number,
    cardBrand: PropTypes.string,
    last4: PropTypes.string,
  }),
  credit: PropTypes.shape({ remaining: PropTypes.number }),
  coachIsViewingOwnSchedule: PropTypes.bool,
  onViewOfferings: PropTypes.func.isRequired,
  onBookAnotherSession: PropTypes.func.isRequired,
}

export default PrivateLessonConfirmation
